import React from 'react';
import firebase from '../../utilities/firebase';
import RequestManager from '../../utilities/requestManager';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';
import ScaleIcon from '@mui/icons-material/Scale';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaceIcon from '@mui/icons-material/Place';
import Divider from '@mui/material/Divider';
import { StateManager } from '../../utilities/stateManager';
import Store from '../../utilities/store';
import algolia from '../../utilities/algolia';
import { StackedDialog, useAutocompleteSlotProps } from '../../utilities/ModalStackContext';
import ShipFromPicker, { DEFAULT_SHIP_FROM, shipFromIsValid, shipFromPayload } from './ShipFromPicker';

const BLUE = '#023e84';
const BUCKET = 'skyway-dev-373d5.appspot.com';

const CARRIER_NAMES = {
  stamps_com: 'USPS',
  ups: 'UPS',
  fedex: 'FedEx',
  dhl_express: 'DHL Express',
};

const CARRIER_STYLES = {
  stamps_com: { label: 'USPS', bg: '#004B87', color: '#ffffff' },
  ups:        { label: 'UPS',  bg: '#351C15', color: '#FFB500' },
  fedex:      { label: 'FedEx', bg: '#4D148C', color: '#ffffff' },
  dhl_express:{ label: 'DHL',  bg: '#D40511', color: '#FFCC00' },
};

const PACKAGE_TYPES = [
  { id: 'envelope', label: 'Envelope',       desc: 'Documents · up to 4 oz', Icon: MailOutlineIcon },
  { id: 'flat',     label: 'Document Flat',  desc: 'Thick docs · up to 8 oz', Icon: DescriptionIcon },
  { id: 'box',      label: 'Small Box',      desc: 'Up to 1 lb',              Icon: InventoryIcon },
];

// Standalone (main-menu) tool only — lets you ship parcels heavier than the document presets.
const CUSTOM_PACKAGE_TYPE = [
  { id: 'custom', label: 'Custom', desc: 'Enter weight', Icon: ScaleIcon },
];

const EMPTY_ADDRESS = {
  name: '', addressLine1: '', cityLocality: '', stateProvince: '', postalCode: '', countryCode: 'US',
};

const fmtAmount = (amt) => {
  if (!amt) return '';
  return `$${(amt.amount || 0).toFixed(2)}`;
};

const fmtDelivery = (days, date) => {
  if (days != null) return `${days} day${days !== 1 ? 's' : ''}`;
  if (date) return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return '';
};

const displayCarrier = (code, friendly) =>
  CARRIER_NAMES[code] || friendly || (code || '').toUpperCase() || 'Carrier';

// ── Carrier logo: uses ShipEngine imageUrl if available, falls back to brand badge ──
function CarrierLogo({ code, friendly, imageUrl }) {
  const style = CARRIER_STYLES[code] || {
    label: (CARRIER_NAMES[code] || friendly || code || 'Carrier').toUpperCase().slice(0, 6),
    bg: '#455a64',
    color: '#ffffff',
  };

  const [imgFailed, setImgFailed] = React.useState(false);

  if (imageUrl && !imgFailed) {
    return (
      <Box
        component="img"
        src={imageUrl}
        alt={style.label}
        onError={() => setImgFailed(true)}
        sx={{ height: 26, objectFit: 'contain', maxWidth: 88 }}
      />
    );
  }

  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      px: 1.5, py: 0.4, borderRadius: 0.75, bgcolor: style.bg,
    }}>
      <Typography sx={{ color: style.color, fontWeight: 900, fontSize: '0.72rem', letterSpacing: 1 }}>
        {style.label}
      </Typography>
    </Box>
  );
}

export default function CreateLabelDialog({ open, onClose, stockNumber, altAddress, onLabelCreated }) {
  // Standalone mode: opened from the main menu with no car. No customer lookup,
  // manual address only, and a custom-weight package option for heavier parcels.
  const standalone = !stockNumber;
  // An alternate shipping address set on the car's DMV tab wins over the buyer.
  const hasAlt = !standalone && !!(altAddress && altAddress.addressLine1);

  const [customerLoading, setCustomerLoading] = React.useState(false);
  const [customer, setCustomer] = React.useState(null);
  const [addressMode, setAddressMode] = React.useState('customer');
  const [address, setAddress] = React.useState(EMPTY_ADDRESS);
  const [packageType, setPackageType] = React.useState('envelope');
  const [shipFrom, setShipFrom] = React.useState(DEFAULT_SHIP_FROM);

  // Custom package (standalone only) — manual weight + optional dimensions.
  const [weightLb, setWeightLb] = React.useState('');
  const [weightOz, setWeightOz] = React.useState('');
  const [dimL, setDimL] = React.useState('');
  const [dimW, setDimW] = React.useState('');
  const [dimH, setDimH] = React.useState('');
  const [gettingRates, setGettingRates] = React.useState(false);
  const [ratesError, setRatesError] = React.useState(null);
  const [rates, setRates] = React.useState(null);
  const [validationStatus, setValidationStatus] = React.useState(null);
  const [selectedRateId, setSelectedRateId] = React.useState(null);
  const [selectedRate, setSelectedRate] = React.useState(null);
  const [creatingLabel, setCreatingLabel] = React.useState(false);
  const [labelResult, setLabelResult] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  // Rates step UI state
  const [rateSearch, setRateSearch] = React.useState('');
  const [rateSort, setRateSort] = React.useState('price');
  const [hiddenCarriers, setHiddenCarriers] = React.useState(new Set());

  // History view (standalone only)
  const [view, setView] = React.useState('create');
  const [history, setHistory] = React.useState(null);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  // Customer search (standalone only) — look up a customer to prefill the address
  const [custSearchInput, setCustSearchInput] = React.useState('');
  const [custOptions, setCustOptions] = React.useState([]);
  const [custSearching, setCustSearching] = React.useState(false);
  const autocompleteSlotProps = useAutocompleteSlotProps();

  const step = labelResult ? 'success' : rates ? 'rates' : 'address';

  // Read through a ref so a new prop object can't restart the open effect.
  const altRef = React.useRef(altAddress);
  altRef.current = altAddress;
  const buildAltAddress = () => ({ ...EMPTY_ADDRESS, ...(altRef.current || {}), countryCode: altRef.current?.countryCode || 'US' });

  // Reset + load deal/customer each time dialog opens
  React.useEffect(() => {
    if (!open) return;
    setRates(null);
    setRatesError(null);
    setValidationStatus(null);
    setSelectedRateId(null);
    setSelectedRate(null);
    setLabelResult(null);
    setCopied(false);
    setGettingRates(false);
    setCreatingLabel(false);
    setAddressMode(standalone ? 'custom' : hasAlt ? 'alternate' : 'customer');
    setShipFrom(DEFAULT_SHIP_FROM);
    setPackageType('envelope');
    setAddress(hasAlt ? buildAltAddress() : EMPTY_ADDRESS);
    setWeightLb('');
    setWeightOz('');
    setDimL('');
    setDimW('');
    setDimH('');
    setCustomer(null);
    setRateSearch('');
    setRateSort('price');
    setHiddenCarriers(new Set());
    setView('create');
    setHistory(null);
    setHistoryLoading(false);
    setCustSearchInput('');
    setCustOptions([]);
    setCustSearching(false);

    // No car → no customer to load; everything is entered manually.
    if (standalone) { setCustomerLoading(false); return; }

    setCustomerLoading(true);
    let cancelled = false;
    firebase.firestore().doc('deals/' + stockNumber).get()
      .then(dealSnap => {
        const buyerId = dealSnap.exists ? dealSnap.data()?.buyer : null;
        if (!buyerId) { if (!cancelled) setCustomerLoading(false); return null; }
        return firebase.firestore().doc('customers/' + buyerId).get();
      })
      .then(custSnap => {
        if (cancelled || !custSnap?.exists) return;
        const c = custSnap.data();
        setCustomer(c);
        // With an alternate address in play the buyer record is only used to
        // fill in a recipient name the alternate address didn't specify.
        if (hasAlt) {
          const buyerName = [c.first_name, c.last_name].filter(Boolean).join(' ');
          setAddress(prev => (prev.name ? prev : { ...prev, name: buyerName }));
        } else {
          applyCustomerAddress(c);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCustomerLoading(false); });

    return () => { cancelled = true; };
  }, [open, stockNumber, standalone, hasAlt]);

  const handleModeChange = (_, newMode) => {
    if (!newMode) return;
    setAddressMode(newMode);
    setRates(null);
    setRatesError(null);
    if (newMode === 'alternate') {
      const alt = buildAltAddress();
      if (!alt.name && customer) alt.name = [customer.first_name, customer.last_name].filter(Boolean).join(' ');
      setAddress(alt);
    } else if (newMode === 'customer' && customer) {
      applyCustomerAddress(customer);
    } else if (newMode === 'custom') {
      setAddress(EMPTY_ADDRESS);
    }
  };

  const setField = (key, val) => setAddress(prev => ({ ...prev, [key]: val }));

  // Map a customer record onto the editable shipping address. The result stays
  // fully editable afterwards — this just prefills the fields.
  const applyCustomerAddress = (c) => {
    const useShipping = c.use_shipping_address && c.shipping_address1;
    setAddress({
      name: [c.first_name, c.last_name].filter(Boolean).join(' '),
      addressLine1: useShipping ? (c.shipping_address1 || '') : (c.address1 || ''),
      cityLocality: useShipping ? (c.shipping_city || '') : (c.city || ''),
      stateProvince: useShipping ? (c.shipping_state || '') : (c.state || ''),
      postalCode: useShipping ? (c.shipping_zip || '') : (c.zip || ''),
      countryCode: useShipping
        ? (c.shipping_country || 'US')
        : ((c.is_international && c.country) ? c.country : 'US'),
    });
  };

  // Standalone: search the customers index, then prefill from the chosen one.
  const handleCustomerSearch = (value) => {
    setCustSearchInput(value);
    if (!value || value.length < 2) { setCustOptions([]); return; }
    setCustSearching(true);
    algolia.client.initIndex('customers').search(value)
      .then(({ hits }) => {
        setCustOptions(hits.map(h => ({
          ...h,
          id: h.objectID,
          label: `${h.first_name || ''} ${h.last_name || ''}`.trim() || '(no name)',
        })));
      })
      .catch(() => setCustOptions([]))
      .finally(() => setCustSearching(false));
  };

  const handleCustomerPick = (hit) => {
    if (!hit) return;
    // Algolia records carry the address fields, so prefill immediately…
    applyCustomerAddress(hit);
    // …then refresh from Firestore in case the index is stale.
    if (hit.id) {
      firebase.firestore().doc('customers/' + hit.id).get()
        .then(snap => { if (snap.exists) applyCustomerAddress(snap.data()); })
        .catch(() => {});
    }
  };

  const addressIsValid = !!(
    address.name && address.addressLine1 && address.cityLocality &&
    address.stateProvince && address.postalCode
  );

  const customWeightOz = (parseFloat(weightLb) || 0) * 16 + (parseFloat(weightOz) || 0);
  const isCustomPackage = packageType === 'custom';
  // Custom package needs a positive weight; presets carry their own fixed weight.
  const packageIsValid = isCustomPackage ? customWeightOz > 0 : true;

  const handleGetRates = async () => {
    setGettingRates(true);
    setRatesError(null);
    setRates(null);
    setSelectedRateId(null);
    setSelectedRate(null);
    try {
      const variables = { toAddress: address, packageType, shipFrom: shipFromPayload(shipFrom) };
      if (isCustomPackage) {
        const hasDims = dimL && dimW && dimH;
        variables.customPackage = {
          weight: { value: customWeightOz, unit: 'ounce' },
          dimensions: hasDims
            ? { length: +dimL, width: +dimW, height: +dimH, unit: 'inch' }
            : undefined,
        };
      }
      const data = await RequestManager.post({
        function: 'getRates',
        variables,
      });
      if (!data.success) throw new Error(data.message || 'Failed to get rates');
      if (!data.rates?.length) throw new Error('No shipping rates available for this address');
      setRates(data.rates);
      const carrierCodes = [...new Set(data.rates.map(r => r.carrierCode))];
      if (carrierCodes.includes('ups')) {
        setHiddenCarriers(new Set(carrierCodes.filter(c => c !== 'ups')));
      } else {
        setHiddenCarriers(new Set());
      }
      setValidationStatus(data.validationStatus);
      if (data.normalizedAddress) setAddress(data.normalizedAddress);
    } catch (err) {
      setRatesError(err.message || 'Failed to get rates');
    } finally {
      setGettingRates(false);
    }
  };

  const handleCreateLabel = async () => {
    if (!selectedRateId) return;
    setCreatingLabel(true);
    try {
      const data = await RequestManager.post({
        function: 'createLabel',
        variables: {
          stockNumber,
          rateId: selectedRateId,
          carrierCode: selectedRate?.carrierCode,
          // Standalone labels have no car — send recipient + service so the
          // history record is readable on its own.
          ...(standalone ? {
            toAddress: address,
            shipFrom: shipFromPayload(shipFrom),
            serviceType: selectedRate?.serviceType,
            carrierFriendlyName: selectedRate?.carrierFriendlyName,
          } : {}),
        },
      });
      if (!data.success) throw new Error(data.message || 'Failed to create label');
      setLabelResult(data);
      onLabelCreated?.(data);
    } catch (err) {
      StateManager.setAlertAndOpen('Failed to create label: ' + err.message, 'error');
    } finally {
      setCreatingLabel(false);
    }
  };

  const handleCopy = () => {
    if (!labelResult?.trackingNumber) return;
    navigator.clipboard.writeText(labelResult.trackingNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openLabelPdf = async (path) => {
    if (!path) return;
    try {
      const url = await firebase.app().storage(BUCKET).ref(path).getDownloadURL();
      window.open(url, '_blank');
    } catch {
      StateManager.setAlertAndOpen('Could not open label PDF', 'error');
    }
  };

  const handleViewLabel = () => openLabelPdf(labelResult?.labelPath);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const rows = await Store.getShippingLabelHistory(50);
      setHistory(rows);
    } catch (err) {
      StateManager.setAlertAndOpen('Failed to load label history: ' + (err.message || err), 'error');
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = () => { setView('history'); loadHistory(); };

  // ── Derived rate data ────────────────────────────────────────────────────────

  const allCarriers = React.useMemo(() => {
    if (!rates) return [];
    const seen = new Set();
    return rates.filter(r => {
      if (seen.has(r.carrierCode)) return false;
      seen.add(r.carrierCode);
      return true;
    });
  }, [rates]);

  const carrierGroups = React.useMemo(() => {
    if (!rates) return [];
    const q = rateSearch.toLowerCase();
    const filtered = rates.filter(r => {
      if (hiddenCarriers.has(r.carrierCode)) return false;
      if (!q) return true;
      return (r.serviceType || '').toLowerCase().includes(q);
    });
    const sorted = [...filtered].sort((a, b) => {
      if (rateSort === 'speed') return (a.deliveryDays ?? 999) - (b.deliveryDays ?? 999);
      return (a.shippingAmount?.amount || 0) - (b.shippingAmount?.amount || 0);
    });
    const map = new Map();
    for (const rate of sorted) {
      const key = rate.carrierCode || 'other';
      if (!map.has(key)) {
        map.set(key, {
          carrierCode: key,
          carrierFriendlyName: rate.carrierFriendlyName,
          imageUrl: rate.imageUrl,
          rates: [],
        });
      }
      map.get(key).rates.push(rate);
    }
    return [...map.values()];
  }, [rates, rateSearch, rateSort, hiddenCarriers]);

  const toggleCarrier = (code) => {
    setHiddenCarriers(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <StackedDialog
      open={open}
      onClose={step === 'success' ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        {view === 'history'
          ? <HistoryIcon sx={{ color: BLUE, fontSize: 22 }} />
          : <LocalShippingIcon sx={{ color: BLUE, fontSize: 22 }} />}
        <Typography variant="h6" sx={{ fontWeight: 700, color: BLUE, flex: 1, fontSize: '1rem' }}>
          {view === 'history' ? 'Label History' : 'Create Shipping Label'}
        </Typography>
        {/* History affordance — standalone tool only, on the first (create) step */}
        {standalone && view === 'create' && step === 'address' && (
          <Tooltip title="Label history">
            <IconButton size="small" onClick={openHistory} sx={{ color: BLUE }}>
              <HistoryIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        {step !== 'success' && (
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>

        {/* ── History view (standalone tool only) ── */}
        {view === 'history' && (
          historyLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={18} sx={{ color: BLUE }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading history…</Typography>
            </Box>
          ) : !history?.length ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <HistoryIcon sx={{ fontSize: 34, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No labels created from this tool yet.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.25} divider={<Divider flexItem />}>
              {history.map(row => (
                <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ flexShrink: 0 }}>
                    <CarrierLogo code={row.carrierCode} friendly={row.carrierFriendlyName} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.toAddress?.name || 'Recipient'}
                      {row.toAddress?.stateProvince ? ` · ${row.toAddress.cityLocality || ''} ${row.toAddress.stateProvince}` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {row.serviceType || row.serviceCode || ''}
                      {row.shippingCost ? ` · ${fmtAmount(row.shippingCost)}` : ''}
                      {row.createdAt ? ` · ${new Date(row.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: BLUE }}>
                        {row.trackingNumber}
                      </Typography>
                      <Tooltip title="Copy tracking">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(row.trackingNumber || '');
                            StateManager.setAlertAndOpen('Tracking number copied', 'success');
                          }}
                          sx={{ color: 'text.secondary', p: 0.25 }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Tooltip title="View label PDF">
                    <IconButton size="small" onClick={() => openLabelPdf(row.labelPath)} sx={{ color: BLUE, flexShrink: 0 }}>
                      <OpenInNewIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          )
        )}

        {/* ── Step: Address + Package ── */}
        {view === 'create' && step === 'address' && (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Ship To
              </Typography>

              {standalone && (
                <Autocomplete
                  freeSolo
                  size="small"
                  sx={{ mb: 2 }}
                  slotProps={autocompleteSlotProps}
                  options={custOptions}
                  filterOptions={(x) => x}
                  loading={custSearching}
                  inputValue={custSearchInput}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
                  onInputChange={(_, value, reason) => { if (reason === 'input') handleCustomerSearch(value); }}
                  onChange={(_, value) => { if (value && typeof value !== 'string') handleCustomerPick(value); }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.label}</Typography>
                        {(option.address1 || option.city) && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {[option.address1, option.city, option.state, option.zip].filter(Boolean).join(', ')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search a customer to prefill (optional)…"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {custSearching ? <CircularProgress size={16} sx={{ color: BLUE }} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}

              {!standalone && (
                <ToggleButtonGroup
                  value={addressMode}
                  exclusive
                  onChange={handleModeChange}
                  size="small"
                  sx={{
                    mb: 2,
                    '& .MuiToggleButton-root': { fontSize: '0.8rem', px: 2, py: 0.75, textTransform: 'none' },
                    '& .MuiToggleButton-root.Mui-selected': { bgcolor: alpha(BLUE, 0.08), color: BLUE, fontWeight: 700 },
                  }}
                >
                  {hasAlt && <ToggleButton value="alternate">Alternate Address</ToggleButton>}
                  <ToggleButton value="customer" disabled={!customer && !customerLoading}>
                    Customer on File
                  </ToggleButton>
                  <ToggleButton value="custom">Custom Address</ToggleButton>
                </ToggleButtonGroup>
              )}

              {addressMode === 'alternate' && (
                <Alert severity="info" icon={<PlaceIcon sx={{ fontSize: 18 }} />} sx={{ fontSize: '0.8rem', mb: 1.5 }}>
                  Using the alternate shipping address set on this vehicle's DMV tab.
                </Alert>
              )}

              {customerLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, mb: 1.5 }}>
                  <CircularProgress size={14} sx={{ color: BLUE }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading customer…</Typography>
                </Box>
              ) : addressMode === 'customer' && !customer ? (
                <Alert severity="warning" sx={{ fontSize: '0.8rem', mb: 1.5 }}>
                  No buyer on file for this vehicle. Switch to Custom Address.
                </Alert>
              ) : null}

              <Stack spacing={1.5}>
                <TextField
                  label="Recipient Name"
                  size="small"
                  fullWidth
                  value={address.name}
                  onChange={(e) => setField('name', e.target.value)}
                />
                <TextField
                  label="Street Address"
                  size="small"
                  fullWidth
                  value={address.addressLine1}
                  onChange={(e) => setField('addressLine1', e.target.value)}
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    label="City"
                    size="small"
                    sx={{ flex: 2 }}
                    value={address.cityLocality}
                    onChange={(e) => setField('cityLocality', e.target.value)}
                  />
                  <TextField
                    label="State"
                    size="small"
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 2 }}
                    value={address.stateProvince}
                    onChange={(e) => setField('stateProvince', e.target.value.toUpperCase())}
                  />
                  <TextField
                    label="ZIP"
                    size="small"
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 10 }}
                    value={address.postalCode}
                    onChange={(e) => setField('postalCode', e.target.value)}
                  />
                </Box>
              </Stack>
            </Box>

            <ShipFromPicker value={shipFrom} onChange={(v) => { setShipFrom(v); setRates(null); setRatesError(null); }} />

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Package Type
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(standalone ? [...PACKAGE_TYPES, ...CUSTOM_PACKAGE_TYPE] : PACKAGE_TYPES).map(({ id, label, desc, Icon }) => {
                  const selected = packageType === id;
                  return (
                    <Box
                      key={id}
                      onClick={() => setPackageType(id)}
                      sx={{
                        flex: 1, cursor: 'pointer', p: 1.5, borderRadius: 1.5, textAlign: 'center',
                        border: '2px solid', borderColor: selected ? BLUE : 'divider',
                        bgcolor: selected ? alpha(BLUE, 0.05) : 'background.paper',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: selected ? BLUE : alpha(BLUE, 0.4) },
                      }}
                    >
                      <Icon sx={{ fontSize: 22, color: selected ? BLUE : 'text.disabled', mb: 0.5 }} />
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: selected ? BLUE : 'text.primary', fontSize: '0.75rem' }}>
                        {label}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.68rem', lineHeight: 1.3 }}>
                        {desc}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {isCustomPackage && (
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <TextField
                      label="Weight (lb)"
                      size="small"
                      type="number"
                      sx={{ flex: 1 }}
                      inputProps={{ min: 0, step: '0.1' }}
                      value={weightLb}
                      onChange={(e) => setWeightLb(e.target.value)}
                    />
                    <TextField
                      label="Weight (oz)"
                      size="small"
                      type="number"
                      sx={{ flex: 1 }}
                      inputProps={{ min: 0, step: '0.1' }}
                      value={weightOz}
                      onChange={(e) => setWeightOz(e.target.value)}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField
                      label="Length (in)"
                      size="small"
                      type="number"
                      sx={{ flex: 1 }}
                      inputProps={{ min: 0, step: '0.1' }}
                      value={dimL}
                      onChange={(e) => setDimL(e.target.value)}
                    />
                    <TextField
                      label="Width (in)"
                      size="small"
                      type="number"
                      sx={{ flex: 1 }}
                      inputProps={{ min: 0, step: '0.1' }}
                      value={dimW}
                      onChange={(e) => setDimW(e.target.value)}
                    />
                    <TextField
                      label="Height (in)"
                      size="small"
                      type="number"
                      sx={{ flex: 1 }}
                      inputProps={{ min: 0, step: '0.1' }}
                      value={dimH}
                      onChange={(e) => setDimH(e.target.value)}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                    Enter total weight. Dimensions are optional but improve rate accuracy.
                  </Typography>
                </Stack>
              )}
            </Box>

            {ratesError && (
              <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{ratesError}</Alert>
            )}
          </Stack>
        )}

        {/* ── Step: Rate Selection ── */}
        {view === 'create' && step === 'rates' && (
          <Stack spacing={1.75}>
            {validationStatus === 'unverified' && (
              <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                Address could not be fully verified — double-check before proceeding.
              </Alert>
            )}

            {/* Search + Sort */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Filter services…"
                value={rateSearch}
                onChange={(e) => setRateSearch(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <ToggleButtonGroup
                value={rateSort}
                exclusive
                onChange={(_, v) => v && setRateSort(v)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': { px: 1.75, fontSize: '0.75rem', textTransform: 'none' },
                  '& .MuiToggleButton-root.Mui-selected': { bgcolor: alpha(BLUE, 0.1), color: BLUE, fontWeight: 700 },
                }}
              >
                <ToggleButton value="price">Price</ToggleButton>
                <ToggleButton value="speed">Speed</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Carrier filter chips — only shown when multiple carriers returned */}
            {allCarriers.length > 1 && (
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {allCarriers.map(r => {
                  const hidden = hiddenCarriers.has(r.carrierCode);
                  return (
                    <Chip
                      key={r.carrierCode}
                      label={displayCarrier(r.carrierCode, r.carrierFriendlyName)}
                      size="small"
                      onClick={() => toggleCarrier(r.carrierCode)}
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: hidden ? 400 : 600,
                        opacity: hidden ? 0.45 : 1,
                        bgcolor: hidden ? 'transparent' : alpha(BLUE, 0.08),
                        color: hidden ? 'text.secondary' : BLUE,
                        border: '1px solid',
                        borderColor: hidden ? 'divider' : alpha(BLUE, 0.25),
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { opacity: 1 },
                      }}
                    />
                  );
                })}
              </Box>
            )}

            {/* Grouped rate cards */}
            {carrierGroups.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No services match your filter.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2.5}>
                {carrierGroups.map(group => (
                  <Box key={group.carrierCode}>
                    {/* Carrier logo header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
                      <CarrierLogo
                        code={group.carrierCode}
                        friendly={group.carrierFriendlyName}
                        imageUrl={group.imageUrl}
                      />
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                        {group.rates.length} service{group.rates.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>

                    {/* Rate rows — service type only, no carrier name repeated */}
                    <Stack spacing={0.75}>
                      {group.rates.map(rate => {
                        const selected = selectedRateId === rate.rateId;
                        const delivery = fmtDelivery(rate.deliveryDays, rate.estimatedDeliveryDate);
                        return (
                          <Box
                            key={rate.rateId}
                            onClick={() => { setSelectedRateId(rate.rateId); setSelectedRate(rate); }}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.25,
                              borderRadius: 1.5, cursor: 'pointer', border: '2px solid',
                              borderColor: selected ? BLUE : 'divider',
                              bgcolor: selected ? alpha(BLUE, 0.05) : 'background.paper',
                              transition: 'all 0.15s',
                              '&:hover': { borderColor: selected ? BLUE : alpha(BLUE, 0.3) },
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: selected ? BLUE : 'text.primary' }}>
                                {rate.serviceType}
                              </Typography>
                              {delivery && (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {delivery} estimated
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: selected ? BLUE : 'text.primary', flexShrink: 0 }}>
                              {fmtAmount(rate.shippingAmount)}
                            </Typography>
                            {selected && <CheckCircleIcon sx={{ fontSize: 18, color: BLUE, flexShrink: 0 }} />}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        )}

        {/* ── Step: Success ── */}
        {view === 'create' && step === 'success' && labelResult && (
          <Stack spacing={2.5} sx={{ py: 1 }}>
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: alpha('#2e7d32', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                <CheckCircleIcon sx={{ fontSize: 30, color: 'success.main' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.dark', fontSize: '1rem', mb: 0.5 }}>
                Label Created
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Your shipping label is ready.
              </Typography>
            </Box>

            <Box sx={{ px: 2, py: 1.5, borderRadius: 1.5, bgcolor: alpha(BLUE, 0.04), border: '1px solid', borderColor: alpha(BLUE, 0.15) }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
                Tracking Number
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 700, color: BLUE, flex: 1 }}>
                  {labelResult.trackingNumber}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                  <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? 'success.main' : 'text.secondary' }}>
                    {copied ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {labelResult.shippingCost && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Shipping Cost</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtAmount(labelResult.shippingCost)}</Typography>
              </Box>
            )}

            <Button
              variant="outlined"
              fullWidth
              startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              onClick={handleViewLabel}
              sx={{ borderColor: BLUE, color: BLUE, '&:hover': { borderColor: BLUE, bgcolor: alpha(BLUE, 0.04) } }}
            >
              View Label (PDF)
            </Button>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
        {view === 'history' && (
          <>
            <Button
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              onClick={() => setView('create')}
              sx={{ color: BLUE }}
            >
              Back to Label
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Close</Button>
          </>
        )}
        {view === 'create' && step === 'address' && (
          <>
            <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleGetRates}
              disabled={!addressIsValid || !packageIsValid || !shipFromIsValid(shipFrom) || gettingRates || customerLoading}
              startIcon={gettingRates ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
              sx={{ bgcolor: BLUE, '&:hover': { bgcolor: alpha(BLUE, 0.85) }, '&.Mui-disabled': { bgcolor: alpha(BLUE, 0.3), color: 'white' } }}
            >
              {gettingRates ? 'Getting Rates…' : 'Get Rates'}
            </Button>
          </>
        )}
        {view === 'create' && step === 'rates' && (
          <>
            <Button
              onClick={() => { setRates(null); setSelectedRateId(null); setSelectedRate(null); }}
              sx={{ color: 'text.secondary' }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateLabel}
              disabled={!selectedRateId || creatingLabel}
              startIcon={creatingLabel ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
              sx={{ bgcolor: BLUE, '&:hover': { bgcolor: alpha(BLUE, 0.85) }, '&.Mui-disabled': { bgcolor: alpha(BLUE, 0.3), color: 'white' } }}
            >
              {creatingLabel ? 'Creating Label…' : 'Create Label'}
            </Button>
          </>
        )}
        {view === 'create' && step === 'success' && (
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: alpha(BLUE, 0.85) } }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </StackedDialog>
  );
}
